import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { PageContext } from '../../context/PageContext';
import { pages, isOptionSelectionPage, isVisualiserPage } from '../../context/pages';
import { useFlowBridge } from './FlowBridgeContext';
import { useUvInsertsContext } from './UvInsertsContext';
import { isCardComplete } from '../navigation/card-completeness';
import { resolveBackDestinationThroughCollapsedOptions } from '../navigation/option-tree';
import { findInlineCard, inlineCardLineBuilder } from '../../packaging/inlineCard';

type PageContextShape = {
  currentPage: string;
  goToPage: (page: string) => void;
  pageSelections: Record<string, number>;
  getPageSelection: (pageName: string) => number | null;
  setPageSelection: (pageName: string, index: number | null) => void;
  getPageAllSelected: (pageName: string) => boolean;
  getPageHasSelections: (pageName: string) => boolean;
  resetPageContext: () => void;
  /** Message typed on step 3's Merry Christmas card (packaging); kept while the journey is open. */
  inlineCardMessage: string;
  setInlineCardMessage: (message: string) => void;
};

/** The page config fields goToPage reads for entry-aware Back and theme preselection. */
type NavPageConfig = {
  backTo?: string;
  backToEntryPage?: boolean;
  options?: { clicksTo?: unknown; preselectTheme?: string }[];
};
const navPages = pages as Record<string, NavPageConfig | undefined>;

type UvInsertsNavContextProviderProps = {
  children: React.ReactNode;
};

/**
 * Provides an override of PageContext backed by the TanStack memory router.
 * Header, Footer, and OptionSelectionPage all call usePage() — this context
 * ensures they read router state and navigate via router.navigate() instead of
 * the outer PageContext.setCurrentPage().
 */
export function UvInsertsNavContextProvider({ children }: UvInsertsNavContextProviderProps) {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const { onFinish, onBack } = useFlowBridge();
  const { textByKey, imageByKey, activeConfig, buildCartLineItem, resolved, setPreselectTheme } = useUvInsertsContext();
  const configByHandle = useMemo(() => {
    const map = new Map<string, { active: boolean }>();
    for (const config of resolved?.configs ?? []) {
      map.set(config.handle, { active: config.active });
    }
    return map;
  }, [resolved]);

  // Page selections for option-selection pages (mirrors PageContext.pageSelections).
  const [pageSelections, setPageSelectionsState] = useState<Record<string, number>>({});
  // Page each `backToEntryPage` page was entered from, so Back returns there (packaging: the predesigned card).
  const entryPageRef = useRef<Record<string, string>>({});
  const [inlineCardMessage, setInlineCardMessage] = useState('');
  // Packaging step 3: the Merry Christmas card when its message is written on the card (null otherwise).
  const inlineCard = useMemo(
    () => findInlineCard({ options: navPages.addPersonalTouch?.options, resolved }),
    [resolved]
  );

  // Derive currentPage from the router location (strip leading '/').
  const rawPathname = routerState.location.pathname;
  const currentPage = (rawPathname.startsWith('/') ? rawPathname.slice(1) : rawPathname) || 'addPersonalTouch';

  const getPageSelection = useCallback((pageName: string): number | null => {
    return pageSelections[pageName] ?? null;
  }, [pageSelections]);

  const setPageSelection = useCallback((pageName: string, index: number | null): void => {
    setPageSelectionsState((prev) => {
      if (index === null) {
        const { [pageName]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [pageName]: index };
    });
  }, []);

  const goToPage = useCallback((destination: string): void => {
    if (destination === 'FINISHED') {
      // Pass the MVVM cart item builder so PageContext can include it in the GIFTWRAP_EVENT.
      // The builder is a closure over current UvInsertsContext state (config, themeIndex, etc.).
      // Skip from a half-built card must not commit it (KB uv-inserts/card-added-without-image-via-skip).
      const keepCard = !isVisualiserPage(currentPage) || isCardComplete({ activeConfig, textByKey, imageByKey });
      onFinish(keepCard ? buildCartLineItem : null);
      return;
    }

    if (destination === 'firstPage') {
      onBack();
      return;
    }

    if (destination === 'skipToAddPersonalTouch') {
      navigate({ to: '/addPersonalTouch' });
      return;
    }

    // CONTINUE on the Merry Christmas card adds it with the message from the card, skipping the picker.
    if (inlineCard && currentPage === 'addPersonalTouch' && destination === inlineCard.route) {
      const templateIds = resolved?.templateIds ?? {};
      onFinish(inlineCardLineBuilder({ card: inlineCard, templateIds, message: inlineCardMessage }));
      return;
    }

    const currentConfig = navPages[currentPage];
    const isBack = destination === currentConfig?.backTo;
    const entryPage = isBack && currentConfig?.backToEntryPage ? entryPageRef.current[currentPage] : undefined;

    const resolvedDestination = resolveBackDestinationThroughCollapsedOptions({
      currentPage,
      destinationPage: entryPage ?? destination,
      configByHandle,
    });

    // Collapsed back-navigation can bubble up past addPersonalTouch to firstPage
    // (e.g. a segment with box inserts but no greetings cards), which lives in the
    // outer gift-wrap flow — this memory router has no route for it.
    if (resolvedDestination === 'firstPage') {
      onBack();
      return;
    }

    if (!isBack && navPages[resolvedDestination]?.backToEntryPage) {
      entryPageRef.current[resolvedDestination] = currentPage;
    }
    // An option can open the next visualiser on a given theme (packaging: Merry Christmas).
    const viaOption = currentConfig?.options?.find((option) => option.clicksTo === resolvedDestination);
    setPreselectTheme(viaOption?.preselectTheme ?? null);

    // Reset option selection state on forward navigation to option-selection pages.
    if (isOptionSelectionPage(resolvedDestination) && resolvedDestination !== 'addPersonalTouch') {
      setPageSelectionsState((prev) => {
        const { [resolvedDestination]: _removed, ...rest } = prev;
        return rest;
      });
    }

    navigate({ to: `/${resolvedDestination}` });
  }, [navigate, onFinish, onBack, buildCartLineItem, currentPage, configByHandle, activeConfig, textByKey, imageByKey, setPreselectTheme, inlineCard, inlineCardMessage, resolved]);

  const getPageHasSelections = useCallback((pageName: string): boolean => {
    if (isOptionSelectionPage(pageName)) {
      return getPageSelection(pageName) !== null;
    }

    if (isVisualiserPage(pageName)) {
      return isCardComplete({ activeConfig, textByKey, imageByKey });
    }

    // Default: pass if we have any personalisation choices (gift wrap selection).
    return false;
  }, [getPageSelection, textByKey, imageByKey, activeConfig]);

  const getPageAllSelected = useCallback((pageName: string): boolean => {
    return getPageHasSelections(pageName);
  }, [getPageHasSelections]);

  const resetPageContext = useCallback((): void => {
    setPageSelectionsState({});
  }, []);

  const value = useMemo<PageContextShape>(() => ({
    currentPage,
    goToPage,
    pageSelections,
    getPageSelection,
    setPageSelection,
    getPageAllSelected,
    getPageHasSelections,
    resetPageContext,
    inlineCardMessage,
    setInlineCardMessage,
  }), [
    currentPage,
    goToPage,
    pageSelections,
    getPageSelection,
    setPageSelection,
    getPageAllSelected,
    getPageHasSelections,
    resetPageContext,
    inlineCardMessage,
  ]);

  // Override PageContext with router-backed values for Header, Footer, and OptionSelectionPage.
  return (
    <PageContext.Provider value={value}>
      {children}
    </PageContext.Provider>
  );
}
